import { useAuth } from '@/hooks/useAuth';
import PasswordChecklist from 'react-password-checklist';

import { Icon } from '@iconify/react';
import {
  Button,
  Card,
  CardBody,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
} from '@nextui-org/react';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
  AuthService,
  type ChangePasswordRequest,
} from '../../services/auth-service';
import { PasswordResetService } from '../../services/password-reset-service';
import { useUserStore } from '../../store/use-user-store';

interface ChangePasswordPageProps {
  requireStrongPassword?: boolean;
}

const ChangePasswordPage: React.FC<ChangePasswordPageProps> = ({
  requireStrongPassword = true,
}) => {
  const [formData, setFormData] = useState<ChangePasswordRequest>({
    current_password: '',
    new_password: '',
    new_password_confirm: '',
  });
  const [isValid, setIsValid] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const { logout } = useAuth();
  const { user } = useUserStore();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isResetLoading, setIsResetLoading] = useState(false);
  const isInvalidPassword =
    isLoading ||
    !formData.current_password ||
    !formData.new_password ||
    !formData.new_password_confirm ||
    formData.new_password !== formData.new_password_confirm ||
    formData.current_password === formData.new_password || // New password should be different
    (requireStrongPassword && !isValid);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleForgotPassword = async () => {
    if (!user?.email) {
      toast.error('User email not found');
      return;
    }

    try {
      setIsResetLoading(true);
      await PasswordResetService.requestReset({ email: user.email });
      onClose();
      toast.success('Password reset email sent successfully');
    } catch (error) {
      console.error('Password reset request failed:', error);
      toast.error(
        (error as { response?: { data?: { error?: string } } }).response?.data
          ?.error || 'Failed to send reset email. Please try again.'
      );
    } finally {
      setIsResetLoading(false);
    }
  };

  const handleChangePassword = async () => {
    // Validate current password is provided
    if (!formData.current_password) {
      toast.error('Please enter your current password');
      return;
    }

    // Validate new password is different from current
    if (formData.current_password === formData.new_password) {
      toast.error('New password must be different from current password');
      return;
    }

    // Validate passwords match
    if (formData.new_password !== formData.new_password_confirm) {
      toast.error('Passwords do not match');
      return;
    }

    // Validate password requirements
    if (requireStrongPassword && !isValid) {
      toast.error('Please meet all password requirements');
      return;
    }

    try {
      setIsLoading(true);
      await AuthService.changePassword(formData);
      await AuthService.logout();
      // Clear all auth and user state
      toast.success(
        'Password changed successfully, please login again with new password',
        {
          duration: 3000,
        }
      );
      logout({ redirectToLogin: false });
      // navigate('/change-password-success');
    } catch (err: any) {
      const errorData = err.response?.data;
      if (errorData && typeof errorData === 'object') {
        // First priority: check for current_password error
        if (
          errorData.current_password &&
          Array.isArray(errorData.current_password)
        ) {
          toast.error(errorData.current_password[0]);
        }
        // Second priority: check first error array in the object
        else {
          const firstErrorKey = Object.keys(errorData)[0];
          if (firstErrorKey && Array.isArray(errorData[firstErrorKey])) {
            toast.error(errorData[firstErrorKey][0]);
          } else {
            // Fallback: general error
            toast.error('Failed to change password');
          }
        }
      } else {
        // Fallback: general error
        toast.error('Failed to change password');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="mt-12 mb-2 flex items-center justify-center">
        {/* <Button
          variant="light"
          onClick={() => navigate(-1)}
          // className="mr-2"
          isIconOnly
        >
          <Icon icon="lucide:chevron-left" className="h-5 w-5" />
        </Button> */}
        <h3 className="text-lg font-bold ">Change Password</h3>
      </div>

      <div className="mx-auto w-full max-w-xl">
        <Card className="px-4" shadow="none" radius="none">
          <CardBody className="px-0">
            <div className="flex flex-col gap-6">
              <div className="space-y-4">
                <div className="flex flex-col gap-2">
                  <Input
                    id="current_password"
                    name="current_password"
                    type={isPasswordVisible.current ? 'text' : 'password'}
                    label="Current Password"
                    placeholder="Enter your current password"
                    value={formData.current_password}
                    onChange={handleInputChange}
                    isRequired
                    variant="bordered"
                    onCopy={(e) => e.preventDefault()}
                    onPaste={(e) => e.preventDefault()}
                    startContent={
                      <Icon icon="mdi:lock-outline" className="text-gray-400" />
                    }
                    endContent={
                      <Button
                        variant="light"
                        isIconOnly
                        className="focus:outline-none"
                        onClick={() =>
                          setIsPasswordVisible((prev) => ({
                            ...prev,
                            current: !prev.current,
                          }))
                        }
                      >
                        <Icon
                          icon={
                            isPasswordVisible.current
                              ? 'mdi:eye-off-outline'
                              : 'mdi:eye-outline'
                          }
                          className="text-gray-400"
                        />
                      </Button>
                    }
                  />

                  <div className="flex justify-start ml-1">
                    <Link
                      as="button"
                      onClick={onOpen}
                      className="text-primary cursor-pointer text-xs"
                    >
                      Forgot password?
                    </Link>
                  </div>
                </div>

                <Input
                  id="new_password"
                  name="new_password"
                  type={isPasswordVisible.new ? 'text' : 'password'}
                  label="New Password"
                  placeholder="Enter your new password"
                  value={formData.new_password}
                  onChange={handleInputChange}
                  isRequired
                  variant="bordered"
                  onCopy={(e) => e.preventDefault()}
                  onPaste={(e) => e.preventDefault()}
                  startContent={
                    <Icon icon="mdi:lock-outline" className="text-gray-400" />
                  }
                  endContent={
                    <Button
                      variant="light"
                      isIconOnly
                      className="focus:outline-none"
                      onClick={() =>
                        setIsPasswordVisible((prev) => ({
                          ...prev,
                          new: !prev.new,
                        }))
                      }
                    >
                      <Icon
                        icon={
                          isPasswordVisible.new
                            ? 'mdi:eye-off-outline'
                            : 'mdi:eye-outline'
                        }
                        className="text-gray-400"
                      />
                    </Button>
                  }
                  validationState={
                    formData.new_password
                      ? formData.current_password &&
                        formData.current_password === formData.new_password
                        ? 'invalid'
                        : isValid
                          ? 'valid'
                          : 'invalid'
                      : undefined
                  }
                  errorMessage={
                    formData.current_password &&
                    formData.new_password &&
                    formData.current_password === formData.new_password
                      ? 'New password must be different from current password'
                      : undefined
                  }
                />

                {formData.new_password && (
                  <div className="rounded-lg border border-gray-200 p-3">
                    <PasswordChecklist
                      rules={[
                        'minLength',
                        'specialChar',
                        'number',
                        'capital',
                        // 'match',
                      ]}
                      minLength={8}
                      value={formData.new_password}
                      valueAgain={formData.new_password_confirm}
                      onChange={(isValid) => setIsValid(isValid)}
                      messages={{
                        minLength: 'At least 8 characters long',
                        specialChar: 'Contains a special character',
                        number: 'Contains a number',
                        capital: 'Contains a capital letter',
                        // match: 'Passwords match',
                      }}
                      className="text-sm"
                    />
                  </div>
                )}

                <Input
                  id="new_password_confirm"
                  name="new_password_confirm"
                  type={isPasswordVisible.confirm ? 'text' : 'password'}
                  label="Confirm New Password"
                  placeholder="Confirm your new password"
                  value={formData.new_password_confirm}
                  onChange={handleInputChange}
                  isRequired
                  variant="bordered"
                  onCopy={(e) => e.preventDefault()}
                  onPaste={(e) => e.preventDefault()}
                  startContent={
                    <Icon
                      icon="mdi:lock-check-outline"
                      className="text-gray-400"
                    />
                  }
                  endContent={
                    <Button
                      variant="light"
                      isIconOnly
                      className="focus:outline-none"
                      onClick={() =>
                        setIsPasswordVisible((prev) => ({
                          ...prev,
                          confirm: !prev.confirm,
                        }))
                      }
                    >
                      <Icon
                        icon={
                          isPasswordVisible.confirm
                            ? 'mdi:eye-off-outline'
                            : 'mdi:eye-outline'
                        }
                        className="text-gray-400"
                      />
                    </Button>
                  }
                  validationState={
                    formData.new_password_confirm
                      ? formData.new_password === formData.new_password_confirm
                        ? 'valid'
                        : 'invalid'
                      : undefined
                  }
                  errorMessage={
                    formData.new_password_confirm &&
                    formData.new_password !== formData.new_password_confirm
                      ? 'Passwords do not match'
                      : undefined
                  }
                  description={
                    formData.new_password_confirm &&
                    formData.new_password === formData.new_password_confirm
                      ? 'Passwords matched'
                      : undefined
                  }
                  // endContent={
                  //   formData.new_password_confirm &&
                  //   formData.new_password === formData.new_password_confirm ? (
                  //     <Icon icon="mdi:check-circle" className="text-success" />
                  //   ) : undefined
                  // }
                />
              </div>

              <div className="flex justify-between gap-3">
                <Button
                  variant="bordered"
                  onClick={() =>
                    setFormData({
                      current_password: '',
                      new_password: '',
                      new_password_confirm: '',
                    })
                  }
                  startContent={<Icon icon="mdi:refresh" />}
                  disabled={isLoading}
                >
                  Clear
                </Button>
                <Button
                  color="primary"
                  isLoading={isLoading}
                  disabled={isInvalidPassword}
                  startContent={!isLoading && <Icon icon="mdi:check" />}
                  onClick={handleChangePassword}
                >
                  {isLoading ? 'Changing Password...' : 'Change Password'}
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <Icon
                icon="mdi:email-outline"
                className="text-2xl text-primary"
              />
              <span>Reset Password</span>
            </div>
          </ModalHeader>
          <ModalBody>
            <p className="text-default-600">
              We'll send a password reset link to:
            </p>
            <div className="bg-default-100 rounded-lg p-3">
              <p className="font-medium text-default-900">
                {user?.email || 'your email address'}
              </p>
            </div>
            <p className="text-small text-default-500">
              Check your inbox and follow the instructions to reset your
              password.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={onClose}>
              Cancel
            </Button>
            <Button
              color="primary"
              onPress={handleForgotPassword}
              isLoading={isResetLoading}
              startContent={
                !isResetLoading && <Icon icon="mdi:email-send-outline" />
              }
            >
              {isResetLoading ? 'Sending...' : 'Send Reset Email'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default ChangePasswordPage;
